import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, Plus, Pencil, Trash2, X } from 'lucide-react';
import { assetUrl } from '../../lib/siteAssets';
import { ImageLibraryPicker } from './ImageLibraryPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', start_local: '', end_local: '', image: '', headline: '', accent: '', subheadline: '', eyebrow: '', cta_primary: '', cta_secondary: '' };
const TEXT_FIELDS = [
  ['headline', 'Headline (first line)'], ['accent', 'Headline italic line'], ['eyebrow', 'Tagline (above headline)'],
  ['cta_primary', 'Primary button label'], ['cta_secondary', 'Secondary button label'],
];
const fmt = (local) => (local ? new Date(local).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '');
const STATUS = { live: 'bg-green-100 text-green-700', upcoming: 'bg-blue-100 text-blue-700', ended: 'bg-gray-100 text-gray-500' };
const inputCls = 'w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none';

const ScheduleForm = ({ token, initial, onClose, onSaved }) => {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const upload = async (file) => {
    const form = new FormData(); form.append('file', file);
    try { const r = await axios.post(`${API}/admin/upload-image`, form, headers); setF((p) => ({ ...p, image: r.data.url })); toast.success('Image uploaded'); }
    catch { toast.error('Upload failed'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (f.id) await axios.put(`${API}/admin/hero-schedules/${f.id}`, f, headers);
      else await axios.post(`${API}/admin/hero-schedules`, f, headers);
      toast.success('Schedule saved'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save schedule'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="hero-schedule-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[#E6DAC5]">
          <h3 className="font-display text-xl text-[#1E1622]">{f.id ? 'Edit Seasonal Hero' : 'New Seasonal Hero'}</h3>
          <button type="button" onClick={onClose} data-testid="hero-schedule-close" className="p-2 hover:bg-[#F5EFE9] rounded-lg"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div><label className="block text-sm font-medium mb-2">Name *</label><input data-testid="hero-schedule-name" value={f.name} onChange={set('name')} placeholder="e.g. Spring Sale" className={inputCls} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-2">Starts (Brisbane time) *</label><input type="datetime-local" data-testid="hero-schedule-start" value={f.start_local} onChange={set('start_local')} className={inputCls} /></div>
            <div><label className="block text-sm font-medium mb-2">Ends (Brisbane time) *</label><input type="datetime-local" data-testid="hero-schedule-end" value={f.end_local} onChange={set('end_local')} className={inputCls} /></div>
          </div>
          <p className="text-xs text-[#1E1622]/50">Leave any field blank to keep the normal homepage version of it.</p>
          <div>
            <label className="block text-sm font-medium mb-2">Hero image</label>
            {f.image && <img src={assetUrl(f.image)} alt="Seasonal hero" data-testid="hero-schedule-image-preview" className="w-full h-36 object-cover rounded-xl mb-2" />}
            <div className="flex flex-wrap items-center gap-3">
              <input type="file" accept="image/*" data-testid="hero-schedule-image-upload" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} className="text-sm" />
              <ImageLibraryPicker token={token} current={f.image} onSelect={(url) => setF((p) => ({ ...p, image: url }))} />
              {f.image && <button type="button" onClick={() => setF({ ...f, image: '' })} data-testid="hero-schedule-image-clear" className="text-xs text-red-600 hover:underline">Remove</button>}
            </div>
          </div>
          {TEXT_FIELDS.map(([k, label]) => (
            <div key={k}><label className="block text-sm font-medium mb-2">{label}</label><input data-testid={`hero-schedule-${k}`} value={f[k]} onChange={set(k)} className={inputCls} /></div>
          ))}
          <div><label className="block text-sm font-medium mb-2">Subheadline</label><textarea rows={3} data-testid="hero-schedule-subheadline" value={f.subheadline} onChange={set('subheadline')} className={inputCls} /></div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-[#E6DAC5]">
          <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full border border-[#E6DAC5]">Cancel</button>
          <button type="button" onClick={save} disabled={saving} data-testid="hero-schedule-save" className="px-5 py-2.5 rounded-full bg-[#3B164C] text-white disabled:opacity-50">{saving ? 'Saving…' : 'Save Schedule'}</button>
        </div>
      </div>
    </div>
  );
};

export const HeroSchedulesPanel = ({ token }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  const load = () => axios.get(`${API}/admin/hero-schedules`, headers).then((r) => setItems(r.data.schedules)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (h) => {
    if (!window.confirm(`Delete "${h.name}"?`)) return;
    await axios.delete(`${API}/admin/hero-schedules/${h.id}`, headers); toast.success('Schedule deleted'); load();
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6" data-testid="hero-schedules-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-semibold text-[#1E1622] flex items-center gap-2"><CalendarClock size={18} className="text-[#C5A059]" /> Seasonal Hero Swap</h3>
          <p className="text-sm text-[#1E1622]/60">Schedule a different homepage hero for sales or launches. It switches on and off automatically (Brisbane time).</p>
        </div>
        <button type="button" onClick={() => setEditing({ ...EMPTY })} data-testid="hero-schedule-add" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3B164C] text-white text-sm whitespace-nowrap"><Plus size={16} /> New Schedule</button>
      </div>
      {items.length === 0 && <p className="text-sm text-[#1E1622]/50" data-testid="hero-schedules-empty">No seasonal heroes scheduled.</p>}
      <div className="space-y-3">
        {items.map((h) => (
          <div key={h.id} data-testid={`hero-schedule-row-${h.id}`} className="flex items-center gap-4 p-3 rounded-xl border border-[#E6DAC5]">
            {h.image ? <img src={assetUrl(h.image)} alt="" className="w-16 h-12 object-cover rounded-lg" /> : <div className="w-16 h-12 rounded-lg bg-[#F5EFE9]" />}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#1E1622] truncate">{h.name}</p>
              <p className="text-xs text-[#1E1622]/60">{fmt(h.start_local)} – {fmt(h.end_local)}</p>
            </div>
            <span data-testid={`hero-schedule-status-${h.id}`} className={`text-xs px-2 py-1 rounded-full capitalize ${STATUS[h.status]}`}>{h.status}</span>
            <button type="button" onClick={() => setEditing({ ...EMPTY, ...h })} data-testid={`hero-schedule-edit-${h.id}`} className="p-2 text-[#3B164C] hover:bg-[#3B164C]/10 rounded-lg"><Pencil size={16} /></button>
            <button type="button" onClick={() => remove(h)} data-testid={`hero-schedule-delete-${h.id}`} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing && <ScheduleForm token={token} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};
